import {Map} from 'immutable';
import keyBy from 'lodash/keyBy';
import countOccurrences from 'utils/countOccurrences';
import React from 'react';
import SAWHeader from './SAWHeader';
import {
  compareName,
  getPeriodDays,
  reduceScores,
  sortCompare,
  sortSkills,
  TIMEFRAME_OPTIONS,
  toggleSortDirection,
} from '../Common';
import SAWBody from './SAWBody';
import SAWScorecardTabs from './SAWScorecardTabs';
import WidgetBase from '../WidgetBase';
import './StrengthsAndWeaknesses.styl';
import User from 'records/User';
import SkillActions from 'actions/SkillActions';
import moment from 'moment';
import ManagerDashboardSource from 'sources/ManagerDashboardSource';
import withCoachingPlanWizard from 'components/coaching/wizard/withCoachingPlanWizard';
import {HISTORY_ACTIONS} from 'components/StatsPage2.utils';
import {SKILLS_ORDER_OPTIONS} from 'components/manager-dashboard/strengths-and-weaknesses/SKILLS_ORDER_OPTIONS';

class StrengthsAndWeaknesses extends WidgetBase {
  constructor(props) {
    super(props);
    this.nameClick = this.nameClick.bind(this);
    this.totalsClick = this.totalsClick.bind(this);
    this.headerClick = this.headerClick.bind(this);
    this.updatePeriod = this.updatePeriod.bind(this);
    this.skillsOrderChange = this.skillsOrderChange.bind(this);
    this.scorecardOptionChange = this.scorecardOptionChange.bind(this);
    this.scoresSelectedOptionChange = this.scoresSelectedOptionChange.bind(this);
    this.callStageSelectedOptionChange = this.callStageSelectedOptionChange.bind(this);
    this.headerTooltip = this.headerTooltip.bind(this);
    this.rowTooltip = this.rowTooltip.bind(this);

    // Default is 4 weeks.
    const defaultTimeframe = TIMEFRAME_OPTIONS[3];
    this.state = {
      sortColumn: 'average-score',
      sortDirection: 'desc',
      selectedTimeframe: defaultTimeframe,
      period: getPeriodDays(defaultTimeframe),
      skillsOrder: SKILLS_ORDER_OPTIONS.by_count.value,
    };
  }

  /**
   * Event handler for when the rep-name is clicked.
   * @param item
   */
  nameClick(item) {
    if (item.active !== false) {
      const state = this.props.fromPath
        ? {
            pathname: this.props.fromPath,
            action: HISTORY_ACTIONS.PUSH,
          }
        : null;
      this.showRepMetrics(this.state.period, item.userId, item.teamId, state);
    } else {
      this.alertUserInactive(item);
    }
  }

  /**
   * Event handler for when the totals is clicked.
   * @param item
   */
  totalsClick(item) {
    const scoredCallsQuery = {
      period: this.state.period,
      startDate: this.props.startDate,
      endDate: this.props.endDate,
      scorecard: this.props.scorecardSelectedOption,
      scoresOption: this.props.scoresSelectedOption,
      isTeamId: false,
      reviewedId: item.userId,
    };
    ManagerDashboardSource.getScoredCallIds(scoredCallsQuery);
  }

  /**
   * Event handler for when the header is clicked.
   * @param column
   */
  headerClick(column) {
    if (this.state.sortColumn !== column) {
      this.setState({
        sortColumn: column,
        sortDirection: 'desc',
      });
    } else {
      this.setState({
        sortDirection: toggleSortDirection(this.state.sortDirection),
      });
    }
  }

  headerTooltip(skill) {
    const managerId = this.getManagerId();
    const {scoresSelectedOption, scorecardSelectedOption} = this.props;
    SkillActions.fetchUserTrend({
      skillName: skill || undefined,
      managerId: managerId,
      from: this.props.startDate || moment().subtract(3, 'month'),
      to: this.props.endDate || moment().add(1, 'day'),
      scoredOption: scoresSelectedOption,
      scorecardId: scorecardSelectedOption,
    });
    return true;
  }

  rowTooltip(user) {
    const {scoresSelectedOption, scorecardSelectedOption} = this.props;
    const managerId = this.getManagerId();
    SkillActions.fetchUserTrend({
      userId: user.userId,
      managerId: managerId,
      from: this.props.startDate || moment().subtract(3, 'month'),
      to: this.props.endDate || moment().add(1, 'day'),
      scoredOption: scoresSelectedOption,
      scorecardId: scorecardSelectedOption,
    });
    return true;
  }

  /**
   * Depending on the "column" compare two rep's stats
   * @param repA User
   * @param repB User
   * @param scores Map of rep scores.  {userId: {skills: {},}, userId: {skills: {}}}
   * @param sortColumn field to sort by
   * @param sortDirection asc / desc
   */
  compareReps(repA, repB, scores, sortColumn, sortDirection) {
    if (sortColumn === null) {
      return 0;
    }
    if (sortColumn === 'rep-name') {
      return compareName(repA.displayName, repB.displayName, sortDirection);
    }
    const repAScores = scores.get(repA.id);
    const repBScores = scores.get(repB.id);
    if (!repAScores || !repBScores) {
      // XXX this seems wrong, but it never happens anyways
      return 0;
    }
    let compared = 0;
    if (sortColumn === 'average-score') {
      compared = sortCompare(repAScores.average, repBScores.average, sortDirection);
    } else if (sortColumn === 'scorecard-count') {
      compared = sortCompare(repAScores.scorecardCount, repBScores.scorecardCount, sortDirection);
    } else {
      // sort by the skill average
      const skillRepA = repAScores.skillScores[sortColumn];
      const averageRepA = skillRepA ? skillRepA.skillAverage : null;
      const skillRepB = repBScores.skillScores[sortColumn];
      const averageRepB = skillRepB ? skillRepB.skillAverage : null;
      compared = sortCompare(averageRepA, averageRepB, sortDirection);
    }
    if (compared === 0) {
      return compareName(repA.displayName, repB.displayName, 'asc');
    }
    return compared;
  }

  getDisplayName(member) {
    const memberObj = new User(member);
    return memberObj.getDisplayName();
  }

  renderEmptyBody() {
    return (
      <div className="empty-widget">
        <p>
          There are no scores for your team in the selected time period. Please score some calls to
          see results here.
        </p>
        <img
          alt="screen by repo"
          src={require('images/manager-dashboard/screen-s-w-by-rep@1x.png')}
        />
      </div>
    );
  }

  /**
   * the date range has changed.  Let's reload data
   * @param selectedTimeframe
   */
  updatePeriod(selectedTimeframe) {
    const period = getPeriodDays(selectedTimeframe);
    this.setState({
      selectedTimeframe,
      period,
    });
    this.getWidgetData({
      widgetId: this.props.widgetId,
      period,
      scorecard: this.props.scorecardSelectedOption,
      callStage: this.props.callStageSelectedOption,
      scoresOption: this.props.scoresSelectedOption,
      widgetType: this.props.widgetType,
    });
  }

  /**
   * New Scorecard was selected
   * @param scorecard Scorecard object
   */
  scorecardOptionChange(scorecard) {
    if (this.props.scorecardOptionChange) {
      this.props.scorecardOptionChange(scorecard);
      return;
    }
    this.getWidgetData({
      widgetId: this.props.widgetId,
      period: this.state.period,
      scorecard: scorecard,
      callStage: this.props.callStageSelectedOption,
      scoresOption: this.props.scoresSelectedOption,
      widgetType: this.props.widgetType,
    });
  }

  /**
   * New scores type was selected
   * @param selection
   */
  scoresSelectedOptionChange(selection) {
    if (this.props.scoresSelectedOptionChange) {
      this.props.scoresSelectedOptionChange(selection);
      return;
    }
    this.getWidgetData({
      widgetId: this.props.widgetId,
      period: this.state.period,
      scorecard: this.props.scorecardSelectedOption,
      callStage: this.props.callStageSelectedOption,
      scoresOption: selection,
      widgetType: this.props.widgetType,
    });
  }

  /**
   * new call stage was selected
   * @param selection
   */
  callStageSelectedOptionChange(selection) {
    if (this.props.callStageSelectedOptionChange) {
      this.props.callStageSelectedOptionChange(selection);
      return;
    }
    this.getWidgetData({
      widgetId: this.props.widgetId,
      period: this.state.period,
      scorecard: this.props.scorecardSelectedOption,
      callStage: selection,
      scoresOption: this.props.scoresSelectedOption,
      widgetType: this.props.widgetType,
    });
  }

  skillsOrderChange(value) {
    this.setState({
      skillsOrder: value,
    });
  }

  renderBodyWithData(
    skills,
    membersScores,
    scorecardsCount,
    teamMembers,
    sortColumn,
    sortDirection
  ) {
    // Change the keys of membersScores so they are in the same format as the keys in teamMembers
    const membersScoresByUserId = Map(
      Object.values(membersScores).map(scoreObj => {
        const {skillScores, ...otherProps} = scoreObj;
        // also have to re-key the skills
        const keyedSkills = keyBy(Object.values(skillScores), item => item.skill);
        const avg = reduceScores(keyedSkills);
        return [scoreObj.userId, {skillScores: keyedSkills, average: avg, ...otherProps}];
      })
    );

    const teamMembersDisplay = teamMembers
      .filter(member => membersScoresByUserId.has(member.userId))
      .map(member => {
        return {
          ...member,
          id: member.userId,
          displayName: this.getDisplayName(member),
        };
      });

    const sortedTeam = teamMembersDisplay.sort((a, b) =>
      this.compareReps(a, b, membersScoresByUserId, sortColumn, sortDirection)
    );

    // Convert teamMembers from an array to a mapping of userId's to users
    const teamMembersByUserId = Map(sortedTeam.map(member => [member.userId, member]));

    const teamOrder = sortedTeam
      .filter(member => membersScoresByUserId.has(member.userId))
      .map(member => member.userId);

    // get the number of occurrences of the skill.
    const skillCounts = countOccurrences(
      Object.values(membersScores).flatMap(o => Object.values(o.skillScores).map(s => s.skill))
    );

    const skillsOrder = sortSkills(
      skills,
      skillCounts,
      this.state.skillsOrder === SKILLS_ORDER_OPTIONS.by_name.value
    );

    return (
      <SAWBody
        {...{teamOrder, skillsOrder, sortDirection, sortColumn}}
        location={this.props.location}
        startDate={this.props.startDate}
        endDate={this.props.endDate}
        members={teamMembersByUserId}
        scores={membersScoresByUserId}
        scorecardsCount={scorecardsCount}
        nameClick={this.nameClick}
        totalsClick={this.totalsClick}
        itemLabel="Rep"
        headerClick={this.headerClick}
        headerTooltip={this.headerTooltip}
        rowTooltip={this.rowTooltip}
        skillsOrderChange={this.skillsOrderChange}
        skillsOrderSelected={this.state.skillsOrder}
        onCreatePlan={this.props.onCreatePlan}
      />
    );
  }

  render() {
    const {
      membersScores,
      scorecardsCount,
      skills,
      teamMembers,
      useScorecardTabs,
      managerId,
      adminId,
    } = this.props;
    const isEmpty = skills.length === 0 || teamMembers.length === 0;

    const {sortColumn, sortDirection, selectedTimeframe} = this.state;

    return (
      <div className="widget strengths-and-weaknesses">
        <SAWHeader
          {...this.props}
          adminView={managerId !== adminId}
          selectedTimeframe={selectedTimeframe}
          updatePeriod={this.updatePeriod}
          scorecardOptionChange={this.scorecardOptionChange}
          scoresSelectedOptionChange={this.scoresSelectedOptionChange}
          callStageSelectedOptionChange={this.callStageSelectedOptionChange}
          useScorecardTabs={useScorecardTabs}
          period={this.state.period}
        />
        {useScorecardTabs && (
          <SAWScorecardTabs
            scorecardOptions={this.props.scorecardOptions}
            scorecardOptionChange={this.scorecardOptionChange}
          />
        )}
        {isEmpty
          ? this.renderEmptyBody()
          : this.renderBodyWithData(
              skills,
              membersScores,
              scorecardsCount,
              teamMembers,
              sortColumn,
              sortDirection
            )}
      </div>
    );
  }
}

StrengthsAndWeaknesses.displayName = 'StrengthsAndWeaknesses';
export default withCoachingPlanWizard(StrengthsAndWeaknesses);
